import { ProductSweepType } from 'components/pages/PCM/Products/ProductFields/ProductShareOfTime/ProductShareOfTime.types';
import { CodeNameModel } from '.';
import { Environment } from './Deal.types';
import {
  DirectSalesCodeNameModel,
  DirectSalesCustomPOIFilterValue,
  DirectSalesFilterScope,
  DirectSalesMediaType,
  DirectSalesPricingMode,
  DirectSalesTargetCriteria,
} from './DirectSalesCampaign.types';

export enum DirectSalesProductTemplateStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export enum DirectSalesProductTemplateRuleType {
  PRICING_MODE = 'pricingMode',
  RANGE = 'range',
  MEDIA_TYPE = 'mediaTypeFilter',
  CHANNEL = 'channelFilter',
  FORMAT = 'productFormatFilter',
  FRAME_LIST = 'unitListFilter',
}

export enum DirectSalesProductTemplateBehaviour {
  FIXED = 'FIXED',
  SET = 'SET',
  ANY = 'ANY',
  DEFAULT = 'DEFAULT',
  DISABLED = 'DISABLED',
  RANGE = 'RANGE',
}

export const productRuleMap: Record<string, RuleType> = {
  channels: 'ChannelFilter',
  mediaType: 'MediaTypeFilter',
  range: 'Range',
  pricingMode: 'PricingMode',
  budget: 'BudgetTarget',
  frames: 'FrameTarget',
  impressions: 'VolumeTarget',
  networks: 'NetworkTarget',
  objectives: 'Objectives',
  poi: 'PoiFilter',
  frameList: 'UnitListFilter',
  tags: 'TagFilter',
  sot: 'SotTarget',
  sweep: 'Sweep',
};

export type DirectSalesProductTemplateMediaFilter = {
  type?: string;
  mediaType: DirectSalesMediaType;
};

export type DirectSalesProductTemplateChannelFilter = {
  type: DirectSalesProductTemplateRuleType;
  name: string;
  id: string;
};

export type DirectSalesProductTemplateTargetFilter = {
  target?: number;
  defaultGreenTolerance: number;
  defaultOrangeTolerance: number;
  defaultPriority: number;
};

export type DirectSalesProductTemplateNetworkFilter = DirectSalesProductTemplateTargetFilter & {
  networkId: string;
  networkName?: string;
};

export type DirectSalesProductFilter = {
  type: string;
  selectionType: DirectSalesFilterScope;
  name: string;
  id: string;
  groupName?: string;
};

export type DirectSalesProductPoiFilter = Omit<DirectSalesProductFilter, 'name'> & {
  radius: number;
  dataProvider: string;
  name: string | null;
};

export type DirectSalesProductTemplateCustomPoiFilterItem = {
  type: 'CustomPoiFilter';
  id: string;
  selectionType: DirectSalesFilterScope;
  values: DirectSalesCustomPOIFilterValue[];
};

export type DirectSalesProductSweepFilter = ProductSweepType;

export type DirectSalesProductSotFixedFilter = {
  type: string;
  target: number;
};

export type DirectSalesProductTemplateFrameListFilter = {
  type: 'UnitListFilter';
  selectionType: DirectSalesFilterScope;
  filename: string | null;
  unitsIds: string[];
};

export type DirectSalesProductTemplateSotFilter = {
  target: number;
  min?: number | null;
  max?: number | null;
  default?: number | null;
  defaultPriority?: number | null;
  defaultGreenTolerance?: number | null;
  defaultOrangeTolerance?: number | null;
};

export type DirectSalesProductTemplateChannelsFilter = DirectSalesProductTemplateChannelFilter[];

export type DirectSalesBehaviourValueKey = 'fixedSet' | 'defaultSet' | 'allowedSet';

export type DirectSalesFixedSet =
  | string[]
  | DirectSalesProductTemplateMediaFilter[]
  | DirectSalesProductTemplateChannelFilter[]
  | DirectSalesProductTemplateTargetFilter[]
  | DirectSalesProductFilter[]
  | DirectSalesTargetCriteria[]
  | DirectSalesProductTemplateNetworkFilter[]
  | DirectSalesProductPoiFilter[]
  | DirectSalesProductTemplateCustomPoiFilterItem[]
  | DirectSalesProductTemplateFrameListFilter[]
  | DirectSalesProductTemplateSotFilter[]
  | DirectSalesProductSotFixedFilter[]
  | DirectSalesProductSweepFilter[];

export type DirectSalesAllowedSet = {
  startDate: string;
  endDate: string;
  floatingDateRange: { floatingFrom: string; floatingTo: string } | null;
}[];

export type DirectSalesRangeSet = {
  min?: number;
  max?: number;
  default?: number;
  defaultGreenTolerance?: number;
  defaultOrangeTolerance?: number;
  defaultPriority?: number;
}[];

export type DirectSalesNetworkRangeSet = {
  min?: number;
  max?: number;
  default?: number;
  defaultGreenTolerance?: number;
  defaultOrangeTolerance?: number;
  defaultPriority?: number;
  networkName: string;
  networkId: string;
  target?: string;
}[];

export type DirectSalesProductRuleFixedSet = DirectSalesProductRules & {
  fixedSet: DirectSalesFixedSet;
};

export type DirectSalesProductRuleDefaultSet = DirectSalesProductRules & {
  defaultSet: DirectSalesDefaultSet;
};

export type DirectSalesDefaultSet = DirectSalesTargetCriteria[];

export type DirectSalesProductRuleAllowedSet = DirectSalesProductRules & {
  allowedSet: DirectSalesAllowedSet;
};

export type DirectSalesProductRuleRangeSet = DirectSalesProductRules & {
  rangeSet: DirectSalesRangeSet;
};

export type DirectSalesProductRules = {
  ruleType: DirectSalesProductTemplateRuleType;
  behaviour: DirectSalesProductTemplateBehaviour;
};

export type DirectSalesProductTemplateLine = {
  lineName: string;
  rules: DirectSalesProductRules[];
};

export type DirectSalesProductTemplateRequest = {
  name: string;
  folder: string;
  status: DirectSalesProductTemplateStatus;
  lines: DirectSalesProductTemplateLine[];
};

export type DirectSalesProductTemplateResponse = {
  id: string;
};

export type ProductIdentifier = {
  id: string;
  name: string;
  folder: string;
  status: string;
};

export type ProductTemplateResponseLine = {
  lineName: string;
  lineReference?: number;
  rules: ProductTemplateRulesResponse[];
};

export type ProductTemplateResponse = {
  lines?: ProductTemplateResponseLine[];
  rules?: ProductTemplateRulesResponse[];
} & ProductIdentifier;

export type ProductTemplate = {
  dealLineId?: string;
  productTemplate?: {
    id: string;
    name: string;
  };
  productLineReference?: number;
} & Omit<ProductTemplateResponse, 'id' | 'name'>;

export type ProductTemplateRulesResponse = {
  ruleType: RuleType;
  behaviour: string;
  fixedSet: DirectSalesFixedSet;
  allowedSet: DirectSalesAllowedSet;
  defaultSet: DirectSalesDefaultSet;
  rangeSet: DirectSalesRangeSet;
};

export type GroupedByFolderProductTemplates = Record<string, CodeNameModel[]>;

type ProductTemplateTargetObjective = {
  objectiveId: number | null;
  priority: {
    code: number | null;
    name: number | null;
  };
  greenTolerance: number | null;
  orangeTolerance: number | null;
};

export type DirectSalesProductTemplateTarget = {
  budgetCriteria: ProductTemplateTargetObjective;
  framesCriteria: ProductTemplateTargetObjective;
  impressionsCriteria: ProductTemplateTargetObjective;
};

export type DirectSalesProductTemplateCurrentLineTarget = {
  impressions: number | null;
  budget: number | null;
  frames: number | null;
};

export type DirectSalesTransformedRuleResponseToDealLine = {
  currentLineData:
    | {
        environments?: Environment[];
        targets: DirectSalesProductTemplateCurrentLineTarget;
      }
    | {};
  directSalesData:
    | {
        dealLineFormats: DirectSalesCodeNameModel[];
        locations: DirectSalesCodeNameModel[];
        pricingMode?: DirectSalesPricingMode;
        mediaType?: DirectSalesMediaType;
        targets: DirectSalesProductTemplateTarget;
      }
    | {};
};

export type RuleType =
  | 'ChannelFilter'
  | 'MediaTypeFilter'
  | 'RevenueStreamFilter'
  | 'FrameSizeFamilyFilter'
  | 'FrameSizeFilter'
  | 'Range'
  | 'PricingMode'
  | 'BudgetTarget'
  | 'FrameTarget'
  | 'VolumeTarget'
  | 'NetworkTarget'
  | 'RouteTownFilter'
  | 'Objectives'
  | 'PoiFilter'
  | 'CustomPoiFilter'
  | 'UnitListFilter'
  | 'SegmentFilter'
  | 'TagFilter'
  | 'SotTarget'
  | 'Sweep';
